/**
 * ThemeInitializer — Applies custom theme in the editor app.
 *
 * Strategy: cache-first, then revalidate from API.
 * 1. Instantly apply theme from localStorage cache (no flash)
 * 2. Fetch fresh theme from API in background
 * 3. If API theme differs from cache, update + re-apply
 *
 * Theme sources (in priority order):
 * - URL param `?theme=<base64>` (iframe embedding)
 * - localStorage cache `custom-theme-config` (instant)
 * - API fetch `GET /tenants/me/theme` (revalidation)
 * - postMessage from parent window (runtime updates)
 */

import { useEffect } from 'react';
import {
    applyTheme,
    cacheCustomTheme,
    getCachedCustomTheme,
    sanitizeThemeConfig,
    setThemeModeAttributes,
    type CustomThemeConfig,
} from '@ai-content-hub/shared-styles';

function getMode(): 'light' | 'dark' {
    return (document.documentElement.getAttribute('data-theme') as 'light' | 'dark') || 'light';
}

// EAA-1679: apps/editor serves both the content-authoring routes (/edit/*,
// /embed/editor, /embed/ai, /h5p/editor) and the actual learner-facing player
// routes (/play/*, /embed/player, /embed/multi, /h5p/play, /h5p/player) from
// the same document/ThemeInitializer. Playful skin is a player-only
// affordance — the editing UI must keep its default chrome regardless of a
// tenant's skin choice, so it stays predictable for content authors. Blocklist
// (not allowlist) the editor routes so a future player-ish route defaults to
// getting the skin rather than silently missing it.
const EDITOR_ROUTE_PATTERN = /\/(edit|embed\/editor|embed\/ai|h5p\/editor)(\/|$|\?)/;

function isEditorRoute(): boolean {
    return EDITOR_ROUTE_PATTERN.test(window.location.pathname);
}

function parseThemeFromURL(): CustomThemeConfig | null {
    try {
        const params = new URLSearchParams(window.location.search);
        const encoded = params.get('theme');
        if (!encoded) return null;
        return JSON.parse(atob(encoded)) as CustomThemeConfig;
    } catch {
        return null;
    }
}

/**
 * EAA-1664 fix (Fable 5 review): every config here is tenant-editable (API
 * response, `?theme=` URL param, or a parent-window postMessage), so it must
 * pass through the same whitelist/pattern sanitizer as web-content's tenant
 * layer before it ever reaches `:root` — `applyTheme()` sanitizes the tenant
 * layer internally (see `mergeThemeLayers`), so no separate sanitize step is
 * needed here.
 *
 * EAA-1677: this used to call the narrower `applyCustomTheme()` (colors
 * only), so the editor app — which is the document iframes/embeds actually
 * boot (EmbedPlayerPage, EmbedContentItem, …) — never stamped `data-skin`,
 * `--radius-*`, or `--font-body`/`--font-heading` on its own `<html>`.
 * Embeds without `:root` access on the *outer* host page therefore never
 * rendered the playful skin. Routing through the full `applyTheme()` cascade
 * applies the same skin/radius/typography layers web-content's
 * CoreThemeProvider already applies, on this app's own document root.
 */
function applyConfig(config: CustomThemeConfig | null): void {
    // Colors/radius/typography still apply everywhere (branding); only the
    // skin/skinTokens layer is excluded on editor/authoring routes — see
    // isEditorRoute() above for why.
    const effectiveConfig =
        config && isEditorRoute() ? { ...config, skin: undefined, skinTokens: undefined } : config;
    applyTheme({ tenant: effectiveConfig }, getMode());
}

/**
 * Fetch tenant theme config from the API via GET /tenants/me/theme.
 */
async function fetchTenantTheme(
    apiToken: string,
    baseUrl: string,
    tenantId: string | null
): Promise<CustomThemeConfig | null> {
    try {
        let base = baseUrl.replace(/\/+$/, '');
        if (!base) {
            base = `${window.location.origin}/api`;
        }

        const isJwt = apiToken.startsWith('eyJ');
        const authHeader = isJwt ? `Bearer ${apiToken}` : `Token ${apiToken}`;

        // WHY: a staff/admin login JWT (isJwt but not a Tenant JWT) has no single "my
        // tenant" for the backend to resolve on its own — forward tenant_id so
        // TenantThemeView's staff fallback (query-param resolution) can pick it up.
        const qs = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : '';
        const res = await fetch(`${base}/tenants/me/theme${qs}`, {
            headers: { Authorization: authHeader },
        });
        if (!res.ok) return null;

        // EAA-1680: GET /tenants/me/theme returns the standard API envelope
        // `{ success, code, message, data: { theme } }`. This raw `fetch()`
        // (unlike sharedApiClient, which unwraps `json.data`) receives the whole
        // envelope, so the theme lives at `json.data.theme` — reading `json.theme`
        // was always undefined, dropping the tenant's skin/typography and leaving
        // the embed player on default chrome even though the API returned 200 with
        // `skin: "playful"`. Unwrap the envelope, falling back to a raw/un-enveloped
        // body for resilience.
        const json = (await res.json()) as {
            data?: { theme?: CustomThemeConfig };
            theme?: CustomThemeConfig;
        };
        return json?.data?.theme ?? json?.theme ?? null;
    } catch {
        return null;
    }
}

function configsEqual(a: CustomThemeConfig | null, b: CustomThemeConfig | null): boolean {
    return JSON.stringify(a) === JSON.stringify(b);
}

interface PostMessageThemeEvent {
    type: 'custom-theme';
    config: CustomThemeConfig | null;
}

/**
 * EAA-755: live light/dark switch from the host page.
 *
 * `?themeMode=` (index.html) only covers the initial paint. Re-reading it
 * would mean changing the iframe `src`, which reloads the embed and drops the
 * learner's in-progress answers — so the host keeps the URL frozen and pushes
 * later toggles through this message instead.
 */
interface PostMessageThemeModeEvent {
    type: 'theme-mode';
    mode: 'light' | 'dark';
}

function isThemeModeMessage(data: unknown): data is PostMessageThemeModeEvent {
    if (typeof data !== 'object' || data === null) return false;
    const record = data as Record<string, unknown>;
    return record.type === 'theme-mode' && (record.mode === 'light' || record.mode === 'dark');
}

function isThemeMessage(data: unknown): data is PostMessageThemeEvent {
    return (
        typeof data === 'object' &&
        data !== null &&
        (data as Record<string, unknown>).type === 'custom-theme'
    );
}

export const ThemeInitializer: React.FC = () => {
    // EAA-1664: the theme cache is now tenant-scoped (`theme:{tenantId}`) so a
    // multi-tenant instance can cache a different tenant's theme per session.
    // EAA-1679 fix: this read `?tenantId=` (camelCase), but IframeBlobWrapper.tsx
    // builds the embed URL with `tenant_id=` (snake_case) — the same param
    // EmbedEditorPage.tsx already reads. The mismatch meant `tenantId` was
    // always null here, so every cache read/write in this file landed on the
    // wrong (legacy, un-scoped) slot instead of the tenant's own.
    const tenantId = new URLSearchParams(window.location.search).get('tenant_id');

    useEffect(() => {
        let cancelled = false;

        // 1. URL param has highest priority (iframe embedding)
        const urlConfig = parseThemeFromURL();
        if (urlConfig) {
            applyConfig(urlConfig);
            cacheCustomTheme(sanitizeThemeConfig(urlConfig), tenantId);
            return;
        }

        // 2. Apply cached theme instantly (no flash on reload)
        const cached = getCachedCustomTheme(tenantId);
        if (cached) {
            applyConfig(cached);
        }

        // 3. Fetch fresh theme from API in background (revalidate)
        const params = new URLSearchParams(window.location.search);
        const apiToken = params.get('apiToken');
        const baseUrl =
            params.get('baseUrl') ||
            import.meta.env.VITE_API_URL ||
            '';

        // EAA-1680 (follow-up finding): only covers `apiToken` present in the initial
        // URL query string. Misses `publicKey` embeds entirely (never attempted here —
        // backend now accepts it via `X-Public-Key`, TenantThemeView was fixed to add
        // PublicKeyAuthentication) and `apiToken` delivered later via PLAYER_INIT
        // postMessage (this effect only runs once on mount) — see
        // specs/active/embed-player-theme-fetch-gap/spec.md.
        if (apiToken) {
            fetchTenantTheme(apiToken, baseUrl, tenantId).then((fresh) => {
                if (cancelled) return;

                // Update cache
                cacheCustomTheme(sanitizeThemeConfig(fresh), tenantId);

                // Re-apply only if different from what we already applied
                if (!configsEqual(fresh, cached)) {
                    applyConfig(fresh);
                }
            });
        }

        return () => { cancelled = true; };
    }, [tenantId]);

    // Listen for postMessage theme updates from parent window
    useEffect(() => {
        const handler = (event: MessageEvent) => {
            if (isThemeModeMessage(event.data)) {
                if (getMode() === event.data.mode) return;
                // Stamping the attribute is enough to re-apply the palette:
                // the MutationObserver below watches `data-theme` and replays
                // the tenant config against the new mode.
                setThemeModeAttributes(event.data.mode);
                return;
            }
            if (!isThemeMessage(event.data)) return;
            applyConfig(event.data.config);
            cacheCustomTheme(sanitizeThemeConfig(event.data.config), tenantId);
        };

        window.addEventListener('message', handler);
        return () => window.removeEventListener('message', handler);
    }, [tenantId]);

    // Re-apply when data-theme attribute changes (light/dark toggle)
    useEffect(() => {
        const observer = new MutationObserver(() => {
            // EAA-755: keep the Tailwind `.dark` class in step with whoever
            // wrote `data-theme` — the boot script and this component both go
            // through setThemeModeAttributes, but H5P and third-party code
            // can set the attribute on its own.
            document.documentElement.classList.toggle('dark', getMode() === 'dark');
            const config = parseThemeFromURL() || getCachedCustomTheme(tenantId);
            if (config) applyConfig(config);
        });

        observer.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ['data-theme'],
        });

        return () => observer.disconnect();
    }, [tenantId]);

    return null;
};

export default ThemeInitializer;
